import { SegmentationModal, SegmentationRule } from '@salecycle/experimental/Modal/SegmentationModal';
import {
  SelectedSegmentationRules,
  SelectedSegmentationRulesProps,
} from '@salecycle/experimental/Segmentation/SelectedSegmentationRules';
import { T } from '@salecycle/experimental/Typography/Typography';
import { Button, Gap, Icon, SvgIcon } from '@salecycle/ui-component-library';
import { allCountries } from 'models';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetAdditionalSegmentation } from 'sections/bulk-retargeting-campaigns/CampaignEditor/state/actions/useSetAdditionalSegmentation';
import {
  TargetAudience,
  TargetAudienceType,
} from 'sections/bulk-retargeting-campaigns/CampaignEditor/state/CampaignEditorState';
import { useCampaignEditorContext } from 'sections/bulk-retargeting-campaigns/CampaignEditor/state/hooks/useCampaignEditorContext';
import { FilterOptionsFactory } from 'sections/offsite-campaigns/offsite-campaign-editor/components/editor/segment-item/segment-filter-items/FilterOptionsFactory';
import styled, { useTheme } from 'styled-components';
import { useGetRulesApplicableForTargetAudience } from '../hooks/useGetRulesApplicableForTargetAudience';
import { smartSegmentatationLabels } from 'models/utils/smartSegmentationLabels';

const AdditionalSegmentBackground = styled.div`
  background-color: ${({ theme }) => theme.designToken.neutral50};
  padding: ${({ theme }) => theme.designToken.spacing5};
  display: flex;
  gap: ${({ theme }) => theme.designToken.spacing2};
  align-items: center;
`;

const EmptyView = ({ toggleSegmentationModal }) => {
  const { t } = useTranslation('campaigns');
  const { state } = useCampaignEditorContext();

  return (
    <div>
      <Button
        variation="secondary"
        leftSideIcon={<SvgIcon name="plus" />}
        onClick={toggleSegmentationModal}
        aria-label={t('bulkRetargetingCampaigns.editor.addSegmentationFilters')}
        disabled={state?.readOnly}
        style={{ whiteSpace: 'nowrap', width: '100%' }}
      >
        {t('bulkRetargetingCampaigns.editor.addSegmentationFilters')}
      </Button>
    </div>
  );
};

const PopulatedView = ({ toggleSegmentationModal, rules, audience }) => {
  const theme = useTheme();
  const { t } = useTranslation('campaigns');

  const { state } = useCampaignEditorContext();

  const getRuleLabelsForOffsiteOptions: SelectedSegmentationRulesProps['getRuleLabels'] = rule => {
    const showValue = s => {
      switch (s.type) {
        case 'geoLocation':
        case 'shippingCountry':
          return allCountries[s.configuration?.value];
        case 'url':
        case 'basketSource':
        case 'basketType':
        case 'basketValue':
        case 'repopulationUrl':
        case 'itemName':
        case 'itemUrl':
        case 'itemId':
        case 'itemCategory':
        case 'legacy':
          return s.configuration?.value;
        case 'smartSegmentation':
          return smartSegmentatationLabels[s.configuration?.value];
        default:
          return s.configuration?.value;
      }
    };

    return {
      type: t(`segmentationRules.types.${rule.type?.replaceAll('.', '_')}`),
      condition: t(`segmentationRules.conditions.${rule.configuration?.condition}`),
      value: showValue(rule),
    };
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.designToken.spacing3,
        backgroundColor: theme.designToken.neutral50,
        padding: theme.designToken.spacing5,
      }}
    >
      <div style={{ display: 'flex', gap: theme.designToken.spacing2, alignItems: 'center' }}>
        <Icon icon="filter" color={theme.designToken.neutral700} size="lg" />
        <T variant="headingH3">{t('bulkRetargetingCampaigns.editor.appliedFilters')}</T>
      </div>
      <div>
        <SelectedSegmentationRules rules={rules} t={t} getRuleLabels={getRuleLabelsForOffsiteOptions} />
      </div>
      <div>
        <Button
          variation="secondary"
          leftSideIcon={<SvgIcon name="pen" />}
          onClick={toggleSegmentationModal}
          aria-label={t('bulkRetargetingCampaigns.editor.editSegmentation')}
          disabled={state?.readOnly}
          style={{ whiteSpace: 'nowrap', width: '100%' }}
        >
          {t('bulkRetargetingCampaigns.editor.editSegmentation')}
        </Button>
      </div>
    </div>
  );
};

interface AdditionalSegmentationProps {
  targetAudience: TargetAudience;
}

export const AdditionalSegmentation = ({ targetAudience }: AdditionalSegmentationProps) => {
  const { t } = useTranslation('campaigns');
  const theme = useTheme();

  const [segmentationModalOpen, setSegmentationModalOpen] = useState(false);
  const toggleSegmentationModal = useCallback(() => setSegmentationModalOpen(!segmentationModalOpen), [
    segmentationModalOpen,
  ]);

  const onSegmentationSave = useSetAdditionalSegmentation();
  const onSegmentationSaveCallback = (rules: SegmentationRule<any, any, any>[][]) => {
    onSegmentationSave(targetAudience?.audience, rules);
  };

  const isBrowseAbandonment = targetAudience?.audience === TargetAudienceType.BrowseAbandonment;
  const isPurchase = targetAudience?.audience === TargetAudienceType.Purchase;
  const optionsFactory = useMemo(() => new FilterOptionsFactory(t, isBrowseAbandonment, true, isPurchase), [
    isBrowseAbandonment,
    isPurchase,
    t,
  ]);

  const rules = useGetRulesApplicableForTargetAudience(targetAudience);

  return (
    <>
      <AdditionalSegmentBackground>
        <div style={{ display: 'flex', gap: theme.designToken.spacing1, alignItems: 'start', flexDirection: 'column' }}>
          <div style={{ display: 'flex', flexDirection: 'row', gap: theme.designToken.spacing1 }}>
            <T variant="headingH3">
              {targetAudience.audience === TargetAudienceType.BasketAbandonment &&
                t('bulkRetargetingCampaigns.editor.abandonedABasket')}
              {targetAudience.audience === TargetAudienceType.BrowseAbandonment &&
                t('bulkRetargetingCampaigns.editor.browsedWithoutPurchasing')}
              {targetAudience.audience === TargetAudienceType.Purchase &&
                t('bulkRetargetingCampaigns.editor.madeAPurchase')}
            </T>
          </div>
          <Gap />
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.designToken.spacing3 }}>
            {rules[0]?.length ? (
              <PopulatedView
                rules={rules}
                audience={targetAudience.audience}
                toggleSegmentationModal={toggleSegmentationModal}
              />
            ) : (
              <EmptyView toggleSegmentationModal={toggleSegmentationModal} />
            )}
          </div>
        </div>
      </AdditionalSegmentBackground>
      <SegmentationModal
        open={segmentationModalOpen}
        onClose={toggleSegmentationModal}
        onSave={onSegmentationSaveCallback}
        initialRules={rules}
        optionsFactory={optionsFactory}
        hasOROption
        hasInListFunctionalty
      />
    </>
  );
};
